import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { useIntl } from 'react-intl';

import css from './SectionReviews.module.css';

const REVIEWS = [
  {
    id: 'r1',
    role: 'creative',
    titleKey: 'reviews.r1.title',
    title: 'Filmmaker',
    stars: 5,
    nameKey: 'reviews.r1.name',
    textKey: 'reviews.r1.text',
    name: 'Austin Divine',
    text:
      'Found the perfect rooftop for our commercial shoot in under 10 minutes. The booking process was seamless and the host was incredibly professional.',
    avatar: '/lovable-uploads/review-avatar-austin.png',
  },
  {
    id: 'r2',
    role: 'host',
    titleKey: 'reviews.r2.title',
    title: 'Studio Owner, Bangkok',
    stars: 5,
    nameKey: 'reviews.r2.name',
    textKey: 'reviews.r2.text',
    name: 'Memie Paweena',
    text:
      'As a host, SceneAccess has been a game-changer. I now have a steady stream of professional productions booking my studio space every week.',
    avatar: '/lovable-uploads/review-avatar-memie.png',
  },
  {
    id: 'r3',
    role: 'creative',
    titleKey: 'reviews.r3.title',
    title: 'Filmmaker',
    stars: 5,
    nameKey: 'reviews.r3.name',
    textKey: 'reviews.r3.text',
    name: 'Rockhound',
    text:
      'We needed a variety of studio and urban locations for a 2-day video production. SceneAccess made the entire process from search to booking effortless.',
    avatar: '/lovable-uploads/review-avatar-rockhound.png',
  },
  {
    id: 'r4',
    role: 'creative',
    titleKey: 'reviews.r4.title',
    title: 'Filmmaker',
    stars: 5,
    nameKey: 'reviews.r4.name',
    textKey: 'reviews.r4.text',
    name: 'Brandon Li',
    text:
      'I needed a studio in Thonglor at the last minute, and the platform made it easy to book quickly. The host was responsive, and everything went smoothly.',
    avatar: '/lovable-uploads/review-avatar-brandon.webp',
  },
  {
    id: 'r5',
    role: 'creative',
    titleKey: 'reviews.r5.title',
    title: 'Content Creator',
    stars: 5,
    nameKey: 'reviews.r5.name',
    textKey: 'reviews.r5.text',
    name: 'Gift Lee',
    text:
      'SceneAccess helped me find a beautiful content studio that matched the mood of my shoot. Clear photos, transparent pricing, and easy communication made planning simple.',
    avatar: '/lovable-uploads/review-avatar-gift.jpg',
  },
  {
    id: 'r6',
    role: 'host',
    titleKey: 'reviews.r6.title',
    title: 'Host, Bangkok',
    stars: 5,
    nameKey: 'reviews.r6.name',
    textKey: 'reviews.r6.text',
    name: 'Arm Kitiikoon',
    text:
      'Listing my space was straightforward, and SceneAccess helped me get bookings from serious creative teams without endless back-and-forth messages.',
    avatar: '/lovable-uploads/review-avatar-arm.jpg',
  },
];

const ROLE_MESSAGES = {
  creative: { id: 'reviews.role.creative', defaultMessage: 'CREATIVE' },
  host: { id: 'reviews.role.host', defaultMessage: 'HOST' },
};

const formatReviewMessage = (intl, id, defaultMessage) =>
  intl.formatMessage({ id, defaultMessage });

const getCardScrollDistance = scroller => {
  const firstCard = scroller?.firstElementChild;
  const secondCard = firstCard?.nextElementSibling;

  if (!firstCard) {
    return 0;
  }

  return secondCard
    ? secondCard.getBoundingClientRect().left - firstCard.getBoundingClientRect().left
    : firstCard.getBoundingClientRect().width;
};

const getCardScrollLeft = (scroller, index) => {
  const firstCard = scroller?.firstElementChild;
  const targetCard = scroller?.children?.[index];

  return firstCard && targetCard ? targetCard.offsetLeft - firstCard.offsetLeft : 0;
};

const ReviewCard = props => {
  const { review, intl } = props;
  const roleMessage = ROLE_MESSAGES[review.role];
  const roleLabel = intl.formatMessage(roleMessage);
  const reviewerName = formatReviewMessage(intl, review.nameKey, review.name);
  const quote = formatReviewMessage(intl, review.textKey, review.text);
  const reviewerTitle = formatReviewMessage(intl, review.titleKey, review.title);

  return (
    <article className={css.card}>
      <div>
        <div className={css.cardTop}>
          <span className={css.badge}>{roleLabel}</span>
          <div
            className={css.stars}
            aria-label={intl.formatMessage(
              { id: 'reviews.starRating', defaultMessage: '{stars} out of 5 stars' },
              { stars: review.stars }
            )}
          >
            {Array.from({ length: review.stars }).map((_, index) => (
              <Star key={`${review.id}-star-${index}`} className={css.star} aria-hidden="true" />
            ))}
          </div>
        </div>

        <blockquote className={css.quote}>&quot;{quote}&quot;</blockquote>
      </div>

      <footer className={css.reviewer}>
        <img className={css.avatar} src={review.avatar} alt={reviewerName} loading="lazy" />
        <div className={css.reviewerInfo}>
          <div className={css.name}>{reviewerName}</div>
          <div className={css.title}>{reviewerTitle}</div>
        </div>
      </footer>
    </article>
  );
};

const SectionReviews = () => {
  const intl = useIntl();
  const scrollRef = useRef(null);
  const [isCompact, setIsCompact] = useState(false);
  const [mobileIndex, setMobileIndex] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const updateCompactMode = event => {
      setIsCompact(event.matches);
    };

    setIsCompact(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateCompactMode);
    } else {
      mediaQuery.addListener(updateCompactMode);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updateCompactMode);
      } else {
        mediaQuery.removeListener(updateCompactMode);
      }
    };
  }, []);

  const showPrevious = () => {
    if (isCompact) {
      const previousIndex = mobileIndex === 0 ? REVIEWS.length - 1 : mobileIndex - 1;
      setMobileIndex(previousIndex);
      scrollRef.current?.scrollTo({
        left: getCardScrollLeft(scrollRef.current, previousIndex),
        behavior: 'smooth',
      });
      return;
    }

    const scroller = scrollRef.current;
    if (!scroller) {
      return;
    }

    const cardScrollDistance = getCardScrollDistance(scroller);
    const loopBoundaryThreshold = cardScrollDistance / 2;

    if (scroller.scrollLeft <= loopBoundaryThreshold) {
      scroller.scrollTo({
        left: scroller.scrollWidth - scroller.clientWidth,
        behavior: 'smooth',
      });
    } else {
      scroller.scrollBy({ left: -cardScrollDistance, behavior: 'smooth' });
    }
  };

  const showNext = () => {
    if (isCompact) {
      const nextIndex = (mobileIndex + 1) % REVIEWS.length;
      setMobileIndex(nextIndex);
      scrollRef.current?.scrollTo({
        left: getCardScrollLeft(scrollRef.current, nextIndex),
        behavior: 'smooth',
      });
      return;
    }

    const scroller = scrollRef.current;
    if (!scroller) {
      return;
    }

    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
    const cardScrollDistance = getCardScrollDistance(scroller);
    const loopBoundaryThreshold = cardScrollDistance / 2;
    const isAtEnd = scroller.scrollLeft >= maxScrollLeft - loopBoundaryThreshold;

    if (isAtEnd) {
      scroller.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      scroller.scrollBy({ left: cardScrollDistance, behavior: 'smooth' });
    }
  };

  const showReview = index => {
    setMobileIndex(index);
    scrollRef.current?.scrollTo({
      left: getCardScrollLeft(scrollRef.current, index),
      behavior: 'smooth',
    });
  };

  const previousLabel = intl.formatMessage({
    id: 'reviews.previous',
    defaultMessage: 'Previous review',
  });
  const nextLabel = intl.formatMessage({ id: 'reviews.next', defaultMessage: 'Next review' });

  return (
    <section id="reviews" className={css.sectionReviews}>
      <div className={css.fixedWidthContainer}>
        <div className={css.header}>
          <div className={css.heading}>
            <h2>{intl.formatMessage({ id: 'reviews.title' })}</h2>
            <p>{intl.formatMessage({ id: 'reviews.subtitle' })}</p>
          </div>

          <div className={css.nav}>
            <button
              type="button"
              className={css.navButton}
              onClick={showPrevious}
              aria-label={previousLabel}
            >
              <ChevronLeft aria-hidden="true" />
            </button>
            <button
              type="button"
              className={css.navButton}
              onClick={showNext}
              aria-label={nextLabel}
            >
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div className={css.carousel}>
        <div ref={scrollRef} className={css.scroller}>
          {REVIEWS.map(review => (
            <ReviewCard key={review.id} review={review} intl={intl} />
          ))}
        </div>
      </div>

      {isCompact ? (
        <div
          className={css.dots}
          aria-label={intl.formatMessage({
            id: 'reviews.pagination',
            defaultMessage: 'Review pagination',
          })}
        >
          {REVIEWS.map((review, index) => {
            const isActive = index === mobileIndex;
            return (
              <button
                key={review.id}
                type="button"
                className={classNames(css.dot, { [css.dotActive]: isActive })}
                aria-label={intl.formatMessage(
                  { id: 'reviews.goToReview', defaultMessage: 'Show review {index}' },
                  { index: index + 1 }
                )}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => showReview(index)}
              />
            );
          })}
        </div>
      ) : null}
    </section>
  );
};

export default SectionReviews;
